import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Stack,
  TextTitle,
  TextBody,
  Icon,
  Search,
  Spacer,
  Avatar,
  Tag,
  IconButton,
  Spinner,
  TabGroup,
  Tab,
  BlockList,
  ResourceCard,
} from '@pypestream/design-system';
import { urqlGql, VoiceAiProvider } from '@pypestream/api-services';
import debounce from 'lodash.debounce';
import InfiniteScroll from 'react-infinite-scroll-component';

import { GenericModal, GenericModalProps } from '..';
import { useAudioPlayback } from '../../../hooks/use-audio-playback';
import { buildVoiceSearchArgs } from './voice-search-args';

import './voice-selector-modal.scss';

export interface VoiceSelectorModalProps extends GenericModalProps {
  /**
   * What the voices are filtered by — one language entry of the Languages
   * block (PSTUDIO-2950). `language` is the key (`es`), not the locale: the v2
   * voice search filters by language and accent, and a locale would hide
   * voices that are verified for the language but tagged with another region.
   */
  selectedValues: {
    audioModel: string;
    language: string;
    accent?: string;
    elevenlabsVoiceId: string;
  };
  onVoiceSelect?: (voiceId: string, voiceMetadata: VoiceAiProvider) => void;
}

export const VoiceSelectorModal: FC<VoiceSelectorModalProps> = ({
  modalRef,
  onClose,
  selectedValues,
  onVoiceSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female'>(
    'all'
  );
  const [allVoices, setAllVoices] = useState<VoiceAiProvider[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  // `fetchError` distinguishes "backend gave us an empty list" (legitimate)
  // from "the request failed" (retryable). Without it, both render as
  // "No voices found" and the user can't tell whether to wait/retry or
  // adjust filters.
  const [fetchError, setFetchError] = useState<unknown>(null);
  const { playAudio, isPlaying } = useAudioPlayback();

  // Everything that changes which voices exist. The modal is shared by every
  // language entry, so a change of entry is a change of query, not of page.
  const voiceQueryKey = [
    selectedValues.audioModel,
    selectedValues.language,
    selectedValues.accent || '',
  ].join('|');
  const prevVoiceQueryKey = useRef(voiceQueryKey);

  // The modal is shared by every language entry and a request can take
  // seconds with its retries, so an answer may arrive after the user moved to
  // another entry (or another search). Only the latest request may write the
  // list; otherwise a Spanish entry could be offered — and saved with — voices
  // fetched for English.
  const latestRequestRef = useRef(0);
  const prevGenderFilter = useRef(genderFilter);
  const prevSearchTerm = useRef(searchTerm);
  const isInitialMount = useRef(true);

  const getVoiceInitials = (name: string): string => {
    if (!name) return '';

    const firstWord = name.split(/[-,\s]/)[0];

    if (firstWord.length <= 2) {
      return firstWord.toUpperCase();
    }

    return firstWord.substring(0, 2).toUpperCase();
  };

  const getElevenLabsData = useCallback(
    async (page: number = 0, append: boolean = false, search: string) => {
      // No language yet (an empty form) would search every voice.
      if (!selectedValues.audioModel || !selectedValues.language) {
        return;
      }

      const request = append
        ? latestRequestRef.current
        : ++latestRequestRef.current;
      const isStale = () => request !== latestRequestRef.current;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setFetchError(null);
      }

      // The Hasura action behind getVoiceAiVoicesV2 proxies to ElevenLabs and
      // has the same flake profile as our other backend reads (webhook
      // timeouts, 502s). Without retry these surface as "No voices found" and
      // are indistinguishable from a legitimate empty result. Retry briefly
      // before giving up.
      const RETRY_DELAYS_MS = [500, 1500, 4000];
      let response: Awaited<
        ReturnType<typeof urqlGql.getVoiceAiVoicesV2>
      > | null = null;
      let lastErr: unknown;

      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        try {
          // v2, not v1: v1 forces language=en and accent=american, which
          // is why this picker used to offer English voices only.
          response = await urqlGql.getVoiceAiVoicesV2({
            args: buildVoiceSearchArgs({
              language: selectedValues.language,
              accent: selectedValues.accent,
              audioModel: selectedValues.audioModel,
              gender: genderFilter,
              search,
              page,
            }),
          });
          // urql surfaces GraphQL errors on `response.error` instead of
          // throwing — treat that as a retryable failure so a webhook 502
          // doesn't masquerade as an empty result.
          if (response.error) {
            throw response.error;
          }
          lastErr = undefined;
          break;
        } catch (err) {
          lastErr = err;
          const delay = RETRY_DELAYS_MS[attempt];
          if (delay !== undefined) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      if (isStale()) return;

      if (lastErr || !response) {
        console.error(
          '[VoiceSelectorModal] getVoiceAiVoicesV2 failed after retries',
          lastErr
        );
        setFetchError(lastErr ?? new Error('Unknown voice fetch error'));
        // Don't wipe previously loaded pages on a pagination failure — let
        // the user keep scrolling whatever already loaded.
        if (!append) {
          setAllVoices([]);
          setHasMoreData(false);
        }
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const newData = response.data?.getVoiceAiVoicesV2 || {};
      const newVoices = newData.voices || [];

      if (append) {
        setAllVoices((prev) => [...prev, ...newVoices]);
      } else {
        setAllVoices(newVoices);
      }
      setHasMoreData(newData.has_more || false);
      setCurrentPage(page);
      setIsLoading(false);
      setIsLoadingMore(false);
    },
    [
      selectedValues.audioModel,
      selectedValues.language,
      selectedValues.accent,
      genderFilter,
    ]
  );

  const resetPagination = useCallback(() => {
    setCurrentPage(0);
    setAllVoices([]);
    setHasMoreData(true);
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((search: string) => {
        resetPagination();
        getElevenLabsData(0, false, search);
      }, 500),
    [resetPagination, getElevenLabsData]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!selectedValues.audioModel) return;

    const voiceQueryChanged = prevVoiceQueryKey.current !== voiceQueryKey;
    const genderFilterChanged = prevGenderFilter.current !== genderFilter;

    prevVoiceQueryKey.current = voiceQueryKey;
    prevGenderFilter.current = genderFilter;

    if (voiceQueryChanged) {
      // A selection made for another language must not be confirmed here.
      setSelectedVoiceId('');
    }

    if (isInitialMount.current || voiceQueryChanged || genderFilterChanged) {
      isInitialMount.current = false;
      debouncedSearch.cancel();
      resetPagination();
      getElevenLabsData(0, false, searchTerm);
    }
  }, [
    selectedValues.audioModel,
    voiceQueryKey,
    genderFilter,
    resetPagination,
    getElevenLabsData,
    searchTerm,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (!selectedValues.audioModel || isInitialMount.current) return;

    const searchTermChanged = prevSearchTerm.current !== searchTerm;
    prevSearchTerm.current = searchTerm;

    if (!searchTermChanged) return;

    if (searchTerm !== '') {
      debouncedSearch(searchTerm);
    } else {
      debouncedSearch.cancel();
      resetPagination();
      getElevenLabsData(0, false, '');
    }
  }, [
    searchTerm,
    selectedValues.audioModel,
    debouncedSearch,
    resetPagination,
    getElevenLabsData,
  ]);

  const loadMoreData = useCallback(() => {
    if (hasMoreData && !isLoadingMore) {
      getElevenLabsData(currentPage + 1, true, searchTerm);
    }
  }, [hasMoreData, isLoadingMore, currentPage, getElevenLabsData, searchTerm]);

  const handleSave = useCallback(() => {
    const selectedVoice = allVoices?.find(
      (voice) => voice?.voice_id === selectedVoiceId
    );
    if (selectedVoiceId && selectedVoice?.voice_id) {
      onVoiceSelect?.(selectedVoice?.voice_id, selectedVoice);
    }
    onClose?.();
  }, [allVoices, selectedVoiceId, onVoiceSelect, onClose]);

  return (
    <GenericModal
      modalRef={modalRef}
      onClose={onClose}
      size="large"
      test-id="voice-selector-modal"
    >
      <TextTitle size="small">Choose a voice</TextTitle>
      <Spacer size="large" />
      <Stack gutter="medium" direction="row" nowrap>
        <Search
          placeholder="Search voices..."
          value={searchTerm}
          onInput={(e) => setSearchTerm(e.target.value)}
          className="c-voice-selector-modal__search"
          size="large"
        />
        <TabGroup variant="flush" className="c-voice-selector-modal__tabs">
          <Tab slot="tab" panel="all" onClick={() => setGenderFilter('all')}>
            All
          </Tab>
          <Tab slot="tab" panel="male" onClick={() => setGenderFilter('male')}>
            Male
          </Tab>
          <Tab
            slot="tab"
            panel="female"
            onClick={() => setGenderFilter('female')}
          >
            Female
          </Tab>
        </TabGroup>
      </Stack>
      <Spacer size="xsmall" />
      <div id="voice-scroll-container" className="c-voice-selector-modal__list">
        {isLoading ? (
          <div className="c-voice-selector-modal__info c-voice-selector-modal__info--full-height">
            <Spinner />
            <Spacer size="xsmall" />
            <TextBody size="small" textAlign="center">
              Loading voices...
            </TextBody>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={allVoices.length}
            next={loadMoreData}
            hasMore={hasMoreData}
            loader={
              <div className="c-voice-selector-modal__info">
                <Spinner />
                <Spacer size="xsmall" />
                <TextBody size="small" textAlign="center">
                  Loading more voices...
                </TextBody>
              </div>
            }
            endMessage={
              <div className="c-voice-selector-modal__info">
                <TextBody size="small" textAlign="center">
                  No more voices to load
                </TextBody>
              </div>
            }
            scrollableTarget="voice-scroll-container"
          >
            <BlockList gutter="xsmall">
              {allVoices.length > 0 ? (
                allVoices.map((voice, index) => {
                  const sound = voice?.preview_url;

                  return (
                    <ResourceCard
                      key={`${voice?.voice_id}-${index}`}
                      interactive
                      selected={selectedVoiceId === voice?.voice_id}
                      onClick={() => {
                        setSelectedVoiceId(voice?.voice_id || '');
                      }}
                      label={voice?.name || ''}
                      description={voice?.description || undefined}
                    >
                      <Avatar
                        label={getVoiceInitials(voice?.name || '')}
                        src={voice?.image_url || ''}
                        size="small"
                        slot="label-prefix"
                      />
                      <Tag variant="default" minimal slot="label-suffix">
                        {voice?.accent || 'no accent'}
                      </Tag>
                      <IconButton
                        slot="action"
                        variant="subtle"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (sound) {
                            playAudio(voice.voice_id || '', sound);
                          }
                        }}
                      >
                        <Icon
                          name={
                            isPlaying(voice?.voice_id || '')
                              ? 'recording'
                              : 'play'
                          }
                          size="xsmall"
                          weight="medium"
                        />
                      </IconButton>
                    </ResourceCard>
                  );
                })
              ) : fetchError ? (
                <div className="c-voice-selector-modal__info">
                  <TextBody textAlign="center">
                    Couldn&apos;t load voices
                  </TextBody>
                  <TextBody variant="secondary" textAlign="center">
                    The voice service is temporarily unavailable.
                  </TextBody>
                  <Spacer size="small" />
                  <Stack justifyContent="center">
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => getElevenLabsData(0, false, searchTerm)}
                      test-id="ps-voice-selector-retry-button"
                    >
                      Retry
                    </Button>
                  </Stack>
                </div>
              ) : (
                <div className="c-voice-selector-modal__info">
                  <TextBody textAlign="center">No voices found</TextBody>
                  <TextBody variant="secondary" textAlign="center">
                    Try adjusting your search terms
                  </TextBody>
                </div>
              )}
            </BlockList>
          </InfiniteScroll>
        )}
      </div>

      <div slot="footer">
        <Stack justifyContent="end" gutter="small">
          <Button
            size="large"
            variant="secondary"
            onClick={onClose}
            test-id="ps-voice-selector-cancel-button"
          >
            Cancel
          </Button>
          <Button
            size="large"
            onClick={handleSave}
            disabled={!selectedVoiceId}
            test-id="ps-voice-selector-confirm-button"
          >
            Confirm
          </Button>
        </Stack>
      </div>
    </GenericModal>
  );
};
